import { uniqBy } from "lodash-es";

import { ProductFeatureModel } from "@api/mappers/products.mapper";

import { getDamPath } from "@utils/configuration/aem";
import { isDefined } from "@utils/helpers";

import { Translate } from "@redux/modules/value/translations/translations.provider";

import { FullProductApiModel } from "lib/@types/service/product/api/product.api.model";
import { ProductFeatureApiModel } from "lib/@types/service/product/api/productFeature.api.model";
import { Media } from "lib/@types/service/shared";

const PRODUCT_ASSEMBLY_DOCUMENTS_KEY = "product.document.assembly";
const PRODUCT_CONDITIONS_DOCUMENTS_KEY = "product.document.conditions";
const PRODUCT_DELIVERY_DOCUMENTS_KEY = "product.document.delivery";
const PRODUCT_PARTS_DOCUMENTS_KEY = "product.document.parts";
const PRODUCT_PERFORMANCE_DOCUMENTS_KEY = "product.document.performance";
const PRODUCT_RETURN_DOCUMENTS_KEY = "product.document.return";
const PRODUCT_SAFETY_DOCUMENTS_KEY = "product.document.safety";
const PRODUCT_SPARE1_DOCUMENTS_KEY = "product.document.spare1";
const PRODUCT_SPARE2_DOCUMENTS_KEY = "product.document.spare2";
const PRODUCT_WARRANTY_DOCUMENTS_KEY = "product.document.warranty";

const CODE_REFERENCES_AND_CODES = "CODE_REFERENCES_AND_CODES";

const FEATURE_KIND_BLACKLIST = ["energy"];

const FSC_IMAGE_URL = "/product-detail-page/default/fsc.png";
const PEFC_IMAGE_URL = "/product-detail-page/default/PEFC.png";
const RECUPEL_IMAGE_URL = "/icons/label/label-recupel.jpg";
const BEBAT_IMAGE_URL = "/icons/label/label-bebat.jpg";

export const getSortedFeatureKinds = (
    product: FullProductApiModel,
    t: Translate,
) => {
    const sortedFeatureKinds = getSortedFeatures(product.features).map(
        f => f.kind,
    );

    sortedFeatureKinds.push({
        sortOrder: 99,
        name: t("productDetail.featureList.title.referencesAndCodes"),
        code: CODE_REFERENCES_AND_CODES,
    });

    return uniqBy(sortedFeatureKinds, f => f.code);
};

const hasCompleteFeatureValue = (feature: ProductFeatureApiModel) => {
    return (
        !!feature.featureValues &&
        feature.featureValues.some(fv => !!fv.formattedValue)
    );
};

const hasValidFeatureKind = (feature: ProductFeatureApiModel) => {
    return (
        !!feature.kind &&
        !!feature.kind.code &&
        !!feature.kind.name &&
        !FEATURE_KIND_BLACKLIST.some(f => feature.code === f)
    );
};

const getSortedFeatures = (features: ProductFeatureApiModel[]) => {
    return features.filter(
        f => !!f.code && hasCompleteFeatureValue(f) && hasValidFeatureKind(f),
    );
};

export const hasDocuments = (product: FullProductApiModel) => {
    let hasAnyDocuments = false;
    const docMap = getAllDocuments(product);
    for (let [_, value] of docMap) {
        if (!hasAnyDocuments) {
            hasAnyDocuments = value.length > 0;
        }
    }
    return hasAnyDocuments;
};

export const getAllDocuments = (product: FullProductApiModel) => {
    const docMap = new Map<string, Array<Media>>();

    addDocuments(
        docMap,
        PRODUCT_ASSEMBLY_DOCUMENTS_KEY,
        product.assemblyDocuments,
    );
    addDocuments(
        docMap,
        PRODUCT_CONDITIONS_DOCUMENTS_KEY,
        product.conditionsDocuments,
    );
    addDocuments(
        docMap,
        PRODUCT_DELIVERY_DOCUMENTS_KEY,
        product.deliveryDocuments,
    );
    addDocuments(docMap, PRODUCT_PARTS_DOCUMENTS_KEY, product.partsDocuments);
    addDocuments(
        docMap,
        PRODUCT_PERFORMANCE_DOCUMENTS_KEY,
        product.performanceDocuments,
    );
    addDocuments(docMap, PRODUCT_RETURN_DOCUMENTS_KEY, product.returnDocuments);
    addDocuments(docMap, PRODUCT_SAFETY_DOCUMENTS_KEY, product.safetyDocuments);
    addDocuments(docMap, PRODUCT_SPARE1_DOCUMENTS_KEY, product.spare1Documents);
    addDocuments(docMap, PRODUCT_SPARE2_DOCUMENTS_KEY, product.spare2Documents);
    addDocuments(
        docMap,
        PRODUCT_WARRANTY_DOCUMENTS_KEY,
        product.warrantyDocuments,
    );

    return docMap;
};

const addDocuments = (
    docMap: Map<string, Array<Media>>,
    key: string,
    documents?: Array<Media> | null,
) => {
    const filterdDocs = documents?.filter(doc => isDefined(doc.url)) || [];

    docMap.set(key, filterdDocs);
};

const isFeatureLabel = (feature: ProductFeatureApiModel) => {
    return (
        feature.label &&
        feature.featureValues.length > 0 &&
        !!feature.featureValues.at(0)?.thumbnailURL &&
        feature.distinctiveFeature
    );
};

export const getFeatureLabels = (product: FullProductApiModel) => {
    const featureLabels = [];

    if (product.fsc) {
        featureLabels.push({
            imageUrl: `${getDamPath()}${FSC_IMAGE_URL}`,
            altText: "productDetail.featurelabels.fsc.altText",
        });
    }

    if (product.pefc) {
        featureLabels.push({
            imageUrl: `${getDamPath()}${PEFC_IMAGE_URL}`,
            altText: "productDetail.featurelabels.pefc.altText",
        });
    }

    if (product.recupel > 0) {
        featureLabels.push({
            imageUrl: `${getDamPath()}${RECUPEL_IMAGE_URL}`,
            altText: "productDetail.featurelabels.recupel.altText",
        });
    }

    if (product.bebat > 0) {
        featureLabels.push({
            imageUrl: `${getDamPath()}${BEBAT_IMAGE_URL}`,
            altText: "productDetail.featurelabels.bebat.altText",
        });
    }

    product.warrantyLabels.forEach(label =>
        featureLabels.push({
            imageUrl: label.url,
            altText: label.altText,
        }),
    );

    product.qualityLabels.forEach(label =>
        featureLabels.push({
            imageUrl: label.url,
            altText: label.altText,
        }),
    );

    product.features
        .filter(isFeatureLabel)
        .map(f => ({
            imageUrl: f.featureValues.at(0)?.thumbnailURL,
            altText: f.featureValues.at(0)?.description,
        }))
        .forEach(l => featureLabels.push(l));

    return featureLabels;
};

const groupFeaturePerFeatureKind = (
    featureMap: Map<string, Array<Partial<ProductFeatureApiModel>>>,
    feature: ProductFeatureApiModel,
) => {
    if (feature.kind) {
        const currentFeatures = featureMap.get(feature.kind.code);

        if (currentFeatures) {
            currentFeatures.push(feature);
        } else {
            featureMap.set(feature.kind.code, [feature]);
        }
    }
};

const getFeaturesReferencesAndCodes = (
    product: FullProductApiModel,
    t: Translate,
) => {
    const featureList: Array<ProductFeatureModel> = [];
    featureList.push({
        name: t("productDetail.featureList.referencesAndCodes.sku"),
        featureValues: [{ formattedValue: product.sku }],
        coreFeature: false,
    });
    featureList.push({
        name: t("productDetail.featureList.referencesAndCodes.ean"),
        featureValues: [{ formattedValue: product.ean }],
        coreFeature: false,
    });
    featureList.push({
        name: t(
            "productDetail.featureList.referencesAndCodes.supplierProductCode",
        ),
        featureValues: [{ formattedValue: product.supplierProductCode }],
        coreFeature: false,
    });
    return featureList;
};

export const getFeaturesGroupedPerKind = (
    product: FullProductApiModel,
    t: Translate,
) => {
    const featureMap = new Map<string, Array<ProductFeatureModel>>();
    const sortedFeatures = getSortedFeatures(product.features);

    sortedFeatures.forEach(feature =>
        groupFeaturePerFeatureKind(featureMap, feature),
    );

    featureMap.set(
        CODE_REFERENCES_AND_CODES,
        getFeaturesReferencesAndCodes(product, t),
    );

    return featureMap;
};
